import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { io } from "socket.io-client";
import MyTable from "../components/MyTable";
import MyBreakcrumbs from "../components/MyBreadcrumbs";
import AddPatientModal from "../components/AddPatientModal";
import { Link } from "react-router-dom";
import BillingForm from "./BillingForm";
import { Tooltip as ReactTooltip } from "react-tooltip";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import {
  checkInPatient,
  createAppointmentWithoutOTP,
  fetchAppointments,
  getNextPatient,
  fetchAllAppointments,
  checkInAppointment,
  changeDoctorForAppointment,
  cancelAppointment,
  serviceHandleNextPatient,
  serviceHandleAddPatientSubmit,
  serviceCreateAppointment,
  serviceFetchDoctorList,
} from "../services/api";
import {
  getClinicIdFromToken,
  getToken,
  getValueFromToken,
} from "../utils/tokenUtils";
import SearchInput from "../components/SearchInput";
import CustomSelect from "../components/CustomSelect";
import InputComponent from "../components/InputComponent";
import { Autocomplete, Button, TextField } from "@mui/material";
import AppointmentDetails from "../components/profile/patientProfile/AppointmentDetails";
import { motion, AnimatePresence } from "framer-motion";
import { FaEdit, FaTimes, FaCheck, FaTimes as FaCancel } from "react-icons/fa";
import { FiPlus } from "react-icons/fi";
import { LuUserCheck } from "react-icons/lu";
import StatusFilterDropdown from "../components/StatusFilterDropdown";
import { addRelatedPatient } from "../../patient/services/patientRegistrationRequesApi";
import { addPatientValidation } from "../utils/validations/addPatientValidation";
import {
  CustomInput,
  downloadFile,
  formatDateGlobal,
  getStartOrEndOfDay,
} from "../utils/commonUtils";
import AntDatePicker from "../components/antDatepicker";
import dayjs from "dayjs";

const Dashboard = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [appointments, setAppointments] = useState([]);
  const [showAddPatientForm, setShowAddPatientForm] = useState(false);
  const [filterInput, setFilterInput] = useState("");
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [patientContactNumber, setPatientContactNumber] = useState("");
  const [doctors, setDoctors] = useState([]);
  const [doctorsForModal, setDoctorsForModal] = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState({
    value: "all",
    label: "All Doctors",
  });
  const [showBillingForm, setShowBillingForm] = useState(false);
  const [selectedPatientId, setSelectedPatientId] = useState(null);
  const [selectedAppointmentDate, setSelectedAppointmentDate] = useState(null);
  const [selectedTab, setSelectedTab] = useState("today");
  const [startDate, setStartDate] = useState(
    dayjs(getStartOrEndOfDay(new Date(), "start"))
  );
  const [endDate, setEndDate] = useState(
    dayjs(getStartOrEndOfDay(new Date(), "end"))
  );
  const [selectedAppointmentDoctor, setSelectedAppointmentDoctor] = useState(
    {}
  );

  console.log(`selectedDoctor`, selectedDoctor);

  const handleEditClick = (appointment) => {
    setSelectedAppointment(appointment);
  };

  const handleCloseAppointmentDetails = () => {
    setSelectedAppointment(null);
  };

  const updateAppointments = async (doctorId) => {
    if (!doctorId || doctorId === "all") {
      // Call a function to fetch all appointments
      fetchAllAppointmentsForClinic();
    } else {
      const filters = { startDate, endDate };
      const response = await fetchAppointments(doctorId, filters);
      // Check if response is not null and has a property `appointments`

      if (response && response.appointments) {
        const updatedAppointments = response.appointments.map(
          (appointment) => ({
            ...appointment,
            doctor_id: appointment.doctor_id,
          })
        );
        setAppointments(updatedAppointments);
      } else {
        // Handle the case where appointments might not be in the response or response is null
        console.error(
          "Failed to fetch appointments or appointments key missing in response"
        );
        setAppointments([]); // Consider setting to an empty array to avoid errors
      }
    }
  };

  const handleBillClick = (patientId, doctorId, appointmentDate) => {
    setSelectedAppointmentDoctor({
      doctor_id: doctorId,
      doctor_name:
        doctorsForModal.find((doctor) => doctor.doctor_id === doctorId)
          ?.first_name +
        " " +
        doctorsForModal.find((doctor) => doctor.doctor_id === doctorId)
          ?.last_name,
    });
    setSelectedPatientId(patientId);
    setSelectedAppointmentDate(appointmentDate);
    setShowBillingForm(true);
  };

  const handleCloseBillingForm = () => {
    setSelectedPatientId(null);
    setShowBillingForm(false);
  };

  const fetchAllAppointmentsForClinic = async () => {
    const clinicId = getClinicIdFromToken();
    const filters = {
      startDate: dayjs(startDate).startOf("day").format("YYYY-MM-DD HH:mm:ss Z"),
      endDate: dayjs(endDate).endOf("day").format("YYYY-MM-DD HH:mm:ss Z"),
    };
    const allAppointmentsResponse = await fetchAllAppointments(
      clinicId,
      filters
    );
    if (allAppointmentsResponse) {
      setAppointments(allAppointmentsResponse.appointments);
    }
  };

  const handleDoctorChange = (doctor) => {
    setSelectedDoctor(doctor);
    if (doctor?.value === "all") {
      // Call a function to fetch all appointments
      fetchAllAppointmentsForClinic();
    } else {
      // Fetch appointments for the selected doctor
      updateAppointments(doctor?.value);
    }
  };

  const handleOpenAddPatientForm = () => {
    setShowAddPatientForm(true);
  };

  const handleCheckIn = async (appointmentId) => {
    try {
      await checkInAppointment(appointmentId);
      // Refresh the appointments list after checking in
      updateAppointments(selectedDoctor?.value);
      setSelectedAppointment(null);
    } catch (error) {
      console.error("Error checking in appointment:", error);
      // Handle error, e.g., show an error message
    }
  };

  const handleChangeDoctor = async (appointmentId, newDoctorId) => {
    try {
      await changeDoctorForAppointment(appointmentId, newDoctorId);
      // Refresh the appointments list after changing the doctor
      updateAppointments(selectedDoctor?.value);
      setSelectedAppointment(null);
    } catch (error) {
      console.error("Error changing doctor for appointment:", error);
      // Handle error, e.g., show an error message
    }
  };

  const handleCancelAppointment = async (appointmentId) => {
    try {
      await cancelAppointment(appointmentId);
      // Refresh the appointments list after canceling the appointment
      updateAppointments(selectedDoctor?.value);
      setSelectedAppointment(null);
    } catch (error) {
      console.error("Error canceling appointment:", error);
      // Handle error, e.g., show an error message
    }
  };

  const handleNextPatient = async (appointmentNumber, timestamp) => {
    const doctorId = selectedDoctor?.value;
    const clinicId = getClinicIdFromToken();

    let params = {
      appointmentNumber: appointmentNumber,
      timestamp: timestamp,
      clinicId: clinicId,
      doctorId: doctorId,
      appointment_number: typeof appointmentNumber === "number" && {
        appointment_number: appointmentNumber,
      },
      timestamp: timestamp && { timestamp: timestamp },
    };
    let response = await serviceHandleNextPatient(params);
    if (response.ok) {
      await updateAppointments(doctorId);
    } else {
      console.error("Failed to process the next patient:", response);
    }
  };

  const handleCheckOut = async (appointmentNumber, doctorId, timestamp) => {
    const clinicId = getClinicIdFromToken();
    const body = {
      clinicId: clinicId,
      doctorId: doctorId,
      appointment_number: typeof appointmentNumber === "number" && {
        appointment_number: appointmentNumber,
      },
      timestamp: timestamp && { timestamp: timestamp },
    };

    const response = await serviceHandleNextPatient(body);
    if (response.ok) {
      // If the call was successful, update the appointments on the frontend
      await updateAppointments(selectedDoctor?.value);
    } else {
      // Handle the error if the API call was not successful
      const errorText = await response.text();
      console.error("Failed to process the next patient:", errorText);
      // Handle error state or display a message to the user
    }
  };

  const [selectedStatuses, setSelectedStatuses] = useState([]);

  const statusOptions = [
    { value: 0, label: "Waiting" },
    { value: 1, label: "In Progress" },
    { value: 2, label: "Completed" },
    { value: 3, label: "Canceled" },
  ];

  const handleStatusChange = (newSelectedOptions) => {
    setSelectedStatuses(newSelectedOptions || []);
  };

  const filteredAppointments = React.useMemo(() => {
    if (selectedStatuses.length === 0) {
      return appointments; // Return all appointments if no filter is selected
    }
    return appointments.filter((appointment) =>
      selectedStatuses.some((status) => status.value === appointment.status)
    );
  }, [appointments, selectedStatuses]);

  const fetchData = async () => {
    const token = getToken();
    const userId = getValueFromToken("user_id");

    if (!token) {
      navigate("/login");
      return;
    }

    const decodedToken = jwtDecode(token);
    const currentDate = new Date();
    if (decodedToken.exp * 1000 < currentDate.getTime()) {
      localStorage.removeItem("token");
      navigate("/login");
      return;
    }

    if (
      decodedToken.role === "doctor" ||
      decodedToken.role === "receptionist"
    ) {
      setIsLoggedIn(true);
    }
    try {
      const doctorsData = await serviceFetchDoctorList(userId, true);

      if (
        doctorsData &&
        doctorsData.data.doctors &&
        doctorsData.data.doctors.length > 0
      ) {
        const allDoctorsOption = {
          doctor_id: "all",
          first_name: "All Doctors",
          last_name: "",
        };
        setDoctors([allDoctorsOption, ...doctorsData.data.doctors]);
        setDoctorsForModal(doctorsData.data.doctors);
        setSelectedDoctor({ value: "all", label: "All Doctors" });
      } else {
        navigate("/clinic/doctor?addDoctor=true");
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      // Handle error or set a state to display an error message
    }
  };

  // Read date from URL query params on mount
  useEffect(() => {
    const dateParam = searchParams.get('date');
    if (dateParam) {
      const date = new Date(dateParam); // Parse string to Date object
      if (!isNaN(date.getTime())) { // Check if valid date
        setStartDate(dayjs(date).startOf("day"));
        setEndDate(dayjs(date).endOf("day"));
        setSelectedTab("custom");
      }
    }
  }, []);

  useEffect(() => {
    fetchData();
    const socket = io(process.env.REACT_APP_API_URL);
    socket.on("appointmentCreated", (data) => {
      const currentClinicId = Number(getClinicIdFromToken());
      const currentDoctorId = Number(getValueFromToken("doctor_id"));
      if (
        Number(data.clinicId) === currentClinicId &&
        Number(data.doctorId) === currentDoctorId
      ) {
        //updateAppointments();
      }
    });

    return () => {
      socket.off("appointmentCreated");
      socket.off("appointmentsUpdated");
      socket.disconnect();
    };
  }, []);

  useEffect(() => {
    if (selectedDoctor?.value === "all") {
      fetchAllAppointmentsForClinic();
    } else {
      updateAppointments(selectedDoctor?.value);
    }
  }, [selectedTab, startDate, endDate]);

  // Keyboard shortcut: Cmd+E or Ctrl+E to toggle Add Patient modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'e') {
        e.preventDefault();
        setShowAddPatientForm(prev => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const doctorIdToName = {};
  doctors.forEach((doctor) => {
    doctorIdToName[
      doctor.doctor_id
    ] = `${doctor.first_name} ${doctor.last_name}`;
  });

  if (!isLoggedIn) {
    // Render nothing or a loading indicator while checking for token and role
    return null; // or <LoadingIndicator />;
  }

  const columns = [
    { Header: "Appointment No", accessor: "seq_no" },
    { Header: "Patient Details", accessor: "patient_details" },
    {
      Header: "Doctor Name",
      accessor: "doctor_id",
      Cell: ({ value, row }) => {
        return (
          // <select
          //   value={value}
          //   onChange={(e) => {
          //     handleDoctorChange(row.original.appointment_id, e.target.value);
          //     handleChangeDoctor(row.original.appointment_id, e.target.value);
          //   }}
          //   className="border border-gray-300 rounded-md px-2 py-1"
          // >
          //   {doctorsForModal.map((doctor) => (
          //     <option key={doctor.doctor_id} value={doctor.doctor_id}>
          //       {doctor.first_name} {doctor.last_name}
          //     </option>
          //   ))}
          // </select>
          <CustomSelect
            value={value}
            isSearchable={false}
            isClearable={false}
            isDisabled={false}
            className={" w-[300px] table_select"}
            onChange={(e) => {
              // handleDoctorChange(e.value);
              handleChangeDoctor(row.original.appointment_id, e.value);
            }}
            options={doctorsForModal.map((doctor) => ({
              value: doctor.doctor_id,
              label: `${doctor.first_name} ${doctor.last_name}`,
            }))}
          />
        );
      },
    },
    {
      Header: "Status",
      accessor: "status",
      Cell: ({ value }) => {
        const getStatusColor = (status) => {
          switch (status) {
            case 0:
              return "bg-brown-100 text-brown-800";
            case 1:
              return "bg-yellow-100 text-yellow-800";
            case 2:
              return "bg-green-100 text-green-800";
            case 3:
              return "bg-red-100 text-red-800";
            default:
              return "";
          }
        };

        const getStatusLabel = (status) => {
          switch (status) {
            case 0:
              return "Waiting"; // cancel, check-in
            case 1:
              return "In Progress"; // check-out, Rx, bill, // check-out status completed == next patient
            case 2:
              return "Completed"; // Rx, bil
            case 3:
              return "Cancelled"; //
            default:
              return "Unknown";
          }
        };

        return (
          <span
            className={`${getStatusColor(
              value
            )} text-[12px] xl:text-sm font-medium me-2 px-2.5 py-0.5 rounded dark:bg-brown-900 dark:text-brown-300`}
          >
            {getStatusLabel(value)}
          </span>
        );
      },
    },
    {
      Header: "Created At",
      accessor: "created_at",
      Cell: ({ value }) => formatDateGlobal(value),
    },
    // {
    //   Header: "Edit",
    //   accessor: "edit",
    //   Cell: ({ row }) => {
    //     return (
    //       <button
    //         className="px-2.5 py-1 bg-[#2E37A4] rounded-md shadow-lg text-[12px] xl:text-sm text-white cursor-pointer hover:bg-[#1a238f]"
    //         onClick={() => handleEditClick(row.original)}
    //       >
    //         Edit
    //       </button>
    //     );
    //   },
    // },
    {
      Header: "Actions",
      accessor: "actions",
      Cell: ({ row }) => {
        const { status, patient_id, appointment_id, doctor_id, created_at } = row.original;

        // Helper function to check if the status is 'Waiting'
        const isWaiting = status === 0;
        const isInProgress = status === 1;
        const isCompleted = status === 2;
        const isCancelled = status === 3;

        return (
          <div className="flex items-center gap-x-2">
            {(isInProgress || isCompleted) && (
              <div className="transition-transform hover:-translate-y-1 ease-in-out">
                <Link
                  to={`/clinic/generatePrescription?patientId=${patient_id}&appointmentId=${appointment_id}&doctorId=${doctor_id}&redirect=clinic/dashboard`}
                  data-tooltip-id="generatePrescription"
                >
                  <div
                    className="inline-flex p-2.5 text-center text-white  border border-yellow-500 rounded-full hover:bg-yellow-100 focus:outline-none
                   items-center justify-center line-clamp-1"
                  >
                    <span className="w-5 h-5 text-yellow-500 inline-flex justify-center items-center ">
                      Rx
                    </span>
                  </div>
                </Link>
                <ReactTooltip
                  id="generatePrescription"
                  place="bottom"
                  content="Prescription"
                  className=" !text-[12px] !px-3 !py-1 "
                />
              </div>
            )}
            {(isInProgress || isCompleted) && (
              <div className="transition-transform hover:-translate-y-1 ease-in-out text-sm cursor-pointer relative">
                <div
                  onClick={() => handleBillClick(patient_id, doctor_id, created_at)}
                  data-tooltip-id="billCheck"
                  className="
                  inline-grid grid-cols-[1fr] p-3 py-1.5 gap-x-2 font-medium tracking-wide text-center text-white capitalize transition-colors duration-300 transform bg-blue-500 rounded-md hover:bg-blue-600 focus:outline-none focus:ring focus:ring-teal-300 focus:ring-opacity-80
                   "
                >
                  <span className="custom-button-icon">Create Bill</span>
                </div>
                <ReactTooltip
                  id="billCheck"
                  place="bottom"
                  content="Create Bill"
                  className=" !text-[12px] !px-3 !py-1 "
                />
              </div>
            )}
            {!isCompleted && !isCancelled && !isInProgress && (
              // {!isInProgress && !isCancelled && (
              <div className="transition-transform hover:-translate-y-1 ease-in-out text-sm cursor-pointer relative">
                <div
                  alt="cancel appointment"
                  data-tooltip-id="cancelAppointment"
                  onClick={() => handleCancelAppointment(appointment_id)}
                  className="inline-grid grid-cols-[1fr_15px] p-3 py-1.5 gap-x-2 items-center text-center text-white  bg-red-500 rounded-md shadow hover:shadow-lg hover:bg-red-600 focus:outline-none
                 "
                >
                  Cancel
                  <FaCancel />
                </div>
                <ReactTooltip
                  id="cancelAppointment"
                  place="bottom"
                  content="Cancel Appointment"
                  className=" !text-[12px] !px-3 !py-1 "
                />
              </div>
            )}
            {isInProgress && (
              <div className="transition-transform hover:-translate-y-1 ease-in-out text-sm cursor-pointer relative">
                <div
                  alt="check-out"
                  data-tooltip-id="checkOut"
                  onClick={() => handleCheckOut(appointment_id, doctor_id)}
                  className="inline-grid grid-cols-[1fr_15px] p-3 py-1.5 gap-x-2 items-center text-center text-white  bg-green-500 rounded-md shadow hover:shadow-lg hover:bg-green-600 focus:outline-none 
                 "
                >
                  Check-Out
                  <FaCheck />
                </div>
                <ReactTooltip
                  id="checkOut"
                  place="bottom"
                  content="Check Out"
                  className=" !text-[12px] !px-3 !py-1 "
                />
              </div>
            )}
            {isWaiting && !isCancelled && (
              <div className="transition-transform hover:-translate-y-1 ease-in-out text-sm cursor-pointer relative">
                <div
                  alt="check-in"
                  data-tooltip-id="checkIn"
                  onClick={() => handleCheckIn(appointment_id)}
                  className="inline-grid grid-cols-[1fr_15px] p-3 py-1.5 gap-x-2 items-center text-center text-white  bg-blue-500 rounded-md shadow hover:shadow-lg hover:bg-blue-600 focus:outline-none 
                  "
                >
                  Check-In
                  <FaCheck />
                </div>

                <ReactTooltip
                  id="checkIn"
                  place="bottom"
                  content="Check in"
                  className=" !text-[12px] !px-3 !py-1 "
                />
              </div>
            )}
          </div>
        );
      },
    },
  ];

  const breadcrumbs = [
    {
      label: "Home",
      href: "#",

      class: "text-[#2E37A4]",
    },
    { label: "Appointments List", class: "text-[#2E37A4]" },
  ];

  const handleFilterChange = (event) => {
    const value = event.target.value || "";
    setFilterInput(value);
  };

  const handleModalClose = () => {
    setShowAddPatientForm(false);
  };

  console.log(doctors, "doctor00"); // Check if doctors array is empty
  console.log(doctors[0], "doctor00"); // Check if doctor objects have first_name and last_name properties
  console.log(selectedDoctor, "doctor00"); // Check if selectedDoctor state is being set correctly
  //

  return (
    <>
      {/* other component */}
      <div
        className={`page-container relative h-full flex flex-auto flex-col px-4 sm:px-6 py-4 sm:py-6 md:px-8 ${
          showBillingForm ? "blur-sm pointer-events-none" : ""
        }`}
      >
        <div className="pt-2 pb-6 flex flex-wrap gap-2 justify-between items-center">
          <MyBreakcrumbs data={breadcrumbs} />
          <div className="inline-flex flex-wrap items-center gap-2">
            <Button
              variant={selectedTab === "today" ? "contained" : "outlined"}
              onClick={() => {
                setSelectedTab("today");
                const today = new Date();
                setStartDate(dayjs(today).startOf("day"));
                setEndDate(dayjs(today).endOf("day"));
              }}
            >
              Today
            </Button>
            <Button
              variant={selectedTab === "yesterday" ? "contained" : "outlined"}
              onClick={() => {
                setSelectedTab("yesterday");
                const yesterday = new Date();
                yesterday.setDate(yesterday.getDate() - 1);
                setStartDate(dayjs(yesterday).startOf("day"));
                setEndDate(dayjs(yesterday).endOf("day"));
              }}
            >
              Yesterday
            </Button>

            <Button
              variant={selectedTab === "custom" ? "contained" : "outlined"}
              onClick={() => {
                if (selectedTab !== "custom") {
                  setSelectedTab("custom");
                  const today = new Date();
                  setStartDate(dayjs(today).startOf("day"));
                  setEndDate(dayjs(today).endOf("day"));
                }
              }}
            >
              Custom
            </Button>
            {selectedTab === "custom" && (
              <>
                <div>
                  <AntDatePicker
                    defaultValue={null}
                    placeholder="select date"
                    value={startDate ? dayjs(startDate) : startDate}
                    onChange={(date, dateString) => {
                      if (endDate && date > endDate) {
                        setEndDate(null);
                      }
                      setStartDate(dateString);
                    }}
                    disabledFutureDate={true}
                    maxDate={dayjs(endDate)}
                  />
                </div>
                <div>
                  <AntDatePicker
                    defaultValue={null}
                    placeholder="select date"
                    value={endDate ? dayjs(endDate) : endDate}
                    onChange={(date, dateStringE) => {
                      if (startDate && date < startDate) {
                        setStartDate(null);
                      }
                      setEndDate(dateStringE);
                    }}
                    minDate={dayjs(startDate)}
                  />
                </div>
              </>
            )}
            {/* <Button
              variant="contained"
              onClick={handleExport}
              style={{ backgroundColor: "#4caf50", color: "white" }}
              startIcon={<CloudDownloadIcon />}
            >
              Export Data
            </Button> */}
            <SearchInput
              filterInput={filterInput}
              handleFilterChange={handleFilterChange}
            />
          </div>
        </div>

        <div className="flex border-2 border-gray-300 shadow-lg rounded-lg bg-blue-50 mb-6">
          <div className=" flex-1 px-4  py-3">
            <span className="  font-normal"> Start Date: </span>
            <span className=" font-semibold">
              {formatDateGlobal(startDate, { short: true })}
            </span>
          </div>
          <div className=" flex-1 px-4  py-3 ">
            <span className=" font-normal"> Last Date: </span>
            <span className=" font-semibold">{formatDateGlobal(endDate, { short: true })}</span>
          </div>
          <div className=" flex-1 px-4  py-3 ">
            <span className=" font-normal"> Doctors: </span>
            <span className=" font-semibold">
              {selectedDoctor?.label || "All Doctors"}
            </span>
          </div>
          <div className=" flex-1 px-4  py-3 ">
            <span className=" font-normal"> Total Appointments: </span>
            <span className=" font-semibold">
              {" "}
              {appointments?.length || 0}{" "}
            </span>
          </div>
        </div>

        <div className="flex ">
          {/* <motion.div
            initial={{ width: "100%" }}
            animate={{ width: selectedAppointment ? "60%" : "100%" }}
            transition={{ duration: 0.5, ease: "easeInOut" }}
            className="bg-white rounded-xl shadow-md shadow-gray-200 overflow-hidden"
          > */}

          <div className="bg-white rounded-xl shadow-md shadow-gray-200 w-full">
            <div className="px-5 py-3 flex flex-wrap md:flex-nowrap justify-between items-center gap-y-5">
              <div className="inline-flex items-center gap-3">
                <div className="text-lg font-semibold text-gray-800">
                  Appointments List
                </div>
              </div>

              <div className="inline-flex items-center gap-3">
                <CustomSelect
                  value={selectedDoctor.value}
                  isSearchable={true}
                  isClearable={false}
                  isDisabled={false}
                  onChange={handleDoctorChange}
                  options={doctors.map((doctor) => ({
                    value: doctor.doctor_id,
                    label: `${doctor.first_name} ${doctor.last_name}`,
                  }))}
                  className={"w-60"}
                />
                <StatusFilterDropdown
                  options={statusOptions}
                  selectedOptions={selectedStatuses}
                  onChange={handleStatusChange}
                />
              </div>
            </div>

            <div className="overflow-auto ">
              <MyTable
                columns={columns}
                data={filteredAppointments}
                onEditClick={handleEditClick}
                filterInput={filterInput}
                handleFilterChange={handleFilterChange}
                onBillClick={handleBillClick}
                type="patientList"
              />
            </div>

            <button
              onClick={handleOpenAddPatientForm}
              type="button"
              className="  text-2xl p-0 fixed right-3 bottom-3 flex items-center justify-center text-white bg-blue-700 rounded-full w-12 h-12 hover:bg-blue-800 dark:bg-blue-600 dark:hover:bg-blue-700 focus:ring-4 focus:ring-blue-300 focus:outline-none dark:focus:ring-blue-800"
            >
              <FiPlus />
              <span className="sr-only">Add Patient</span>
            </button>

            <AddPatientModal
              doctors={doctorsForModal}
              isOpen={showAddPatientForm}
              onClose={handleModalClose}
              fetchAllAppointmentsForClinic={fetchAllAppointmentsForClinic}
            />

            {selectedDoctor?.value !== "all" && (
              <div className="px-5 py-5">
                <button
                  onClick={handleNextPatient}
                  className="min-w-[131px] text-base font-medium rounded-lg py-2 px-5 bg-[#2E37A4] text-white"
                >
                  Next Patient
                </button>
              </div>
            )}
          </div>
        </div>
        {/* </motion.div> */}
      </div>

      <AnimatePresence>
        {selectedAppointment && (
          <motion.div
            initial={{ opacity: 1, right: "-500px" }}
            animate={{ opacity: 1, right: "0px" }}
            exit={{ opacity: 1, right: "-500px" }}
            transition={{ duration: 0.5, ease: "easeInOut" }}
            className="bg-white shadow-md rounded-none fixed right-0 top-0 h-full  z-10 w-full lg:w-[500px] xl:w-[500px] 2xl:w-[500px]"
          >
            {/* <button
                className="absolute top-2 right-2 text-gray-600 hover:text-gray-800"
                onClick={handleCloseAppointmentDetails}
              >
                <FaTimes className="text-xl" />
              </button> */}
            <AppointmentDetails
              appointment={selectedAppointment}
              doctorIdToName={doctorIdToName}
              doctors={doctors}
              onClose={handleCloseAppointmentDetails}
              onCheckIn={() =>
                handleCheckIn(selectedAppointment.appointment_id)
              }
              onChangeDoctor={handleChangeDoctor}
              onCancelAppointment={() =>
                handleCancelAppointment(selectedAppointment.appointment_id)
              }
            />
          </motion.div>
        )}
      </AnimatePresence>

      {showBillingForm && (
        <BillingForm
          patientId={selectedPatientId}
          appointmentDate={selectedAppointmentDate}
          addDefaultConsultationService={true}
          selectedAppointmentDoctor={selectedAppointmentDoctor}
          onClose={handleCloseBillingForm}
        />
      )}

      {/* other component */}
    </>
  );
};

export default Dashboard;
